<template>
    <div class="htwb-card-home">
        <!-- 頂部導航 -->
        <div class="top-nav">
            <div class="nav-left">
                <h1 class="title">HTcard</h1>
            </div>
            <div class="nav-right">
                <!-- 未登入狀態 -->
                <div v-if="!isLogin" class="login-section">
                    <button class="login-btn" @click="loginTMID">
                        {{ $t('htwbcard.login') }}
                    </button>
                </div>

                <!-- 已登入狀態 -->
                <div v-else class="user-section">
                    <div class="user-info" @click="toggleUserMenu">
                        <img v-if="profile.picture" :src="profile.picture" class="avatar" alt="avatar">
                        <div v-else class="avatar-placeholder">{{ profile.name?.charAt(0) }}</div>
                        <span class="user-name">{{ profile.name }}</span>
                    </div>

                    <!-- 用戶選單 -->
                    <div v-show="showUserMenu" class="user-menu">
                        <div class="menu-item">{{ profile.name }}</div>
                        <div class="menu-divider"></div>
                        <div class="menu-item menu-action" @click="logout">
                            {{ $t('htwbcard.logout') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 內容頁 -->
        <div class="content">
            <!-- CardCollect 網格視圖 -->
            <div class="cardcollect-grid" v-show="isLogin">
                <!-- 搜尋框 -->
                <div class="search-container">
                    <div class="search-box">
                        <!-- 放大鏡圖示 -->
                        <div class="search-icon">
                            <svg-icon icon-class="search" />
                        </div>
                        <input 
                            type="text" 
                            v-model="searchText" 
                            :placeholder="$t('htwbcard.searchPlaceholder')"
                            @input="onSearchInput"
                            @keyup.enter="performSearch"
                            class="search-input"
                        />
                        <button 
                            v-if="searchText" 
                            @click="clearSearch" 
                            class="clear-search-btn"
                            :title="$t('htwbcard.clearSearch')"
                        >
                            <svg-icon icon-class="close" />
                        </button>
                        
                        <!-- 搜尋結果資訊 -->
                        <div v-if="searchText" class="search-results-count">
                            <span v-if="isSearching">{{ $t('htwbcard.searching') }}</span>
                            <span v-else>{{ $t('htwbcard.searchResults', { count: cardCollectList.length }) }}</span>
                        </div>
                    </div>
                </div>

                <div class="mindmapcard-wrap">
                    <!-- 新增收集板按鈕 -->
                    <div class="card-item add-card" @click="showCreateCardCollectModal = true">
                        <div class="add-card-content">
                            <div class="add-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </div>
                            <h5 v-html="$t('htwbcard.addCollectBoard')"></h5>
                        </div>
                    </div>

                    <!-- CardCollect 卡片列表 -->
                    <div class="card-item" v-for="cardCollect in cardCollectList" :key="cardCollect.id">
                        <div class="collect-top" @click="enterCardCollect(cardCollect)">
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z">
                                </path>
                            </svg>
                        </div>

                        <div class="card-textwrap">
                            <h5 @click="enterCardCollect(cardCollect)">{{ cardCollect.name }}</h5>
                            <p class="update-time">{{ getTime(cardCollect.updateTime) }}</p>
                        </div>

                        <div class="more-btn" @click="openCardCollectActionMenu(cardCollect, $event)">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="1"></circle>
                                <circle cx="12" cy="5" r="1"></circle>
                                <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                        </div>

                        <p class="share">
                            <svg-icon :iconClass="cardCollect.openSubmit ? 'eye' : 'close-eye'"></svg-icon>
                            <span class="coworkmode-note-text">{{ cardCollect.openSubmit ? $t('htwbcard.collectBoardStatus.openSubmit') : $t('htwbcard.collectBoardStatus.closeSubmit') }}</span>
                        </p>
                    </div>
                </div>

                <!-- 分頁按鈕 - 右下角固定位置 -->
                <div class="pagination-container" v-if="cardCollectList.length > 0 && pageBtns.length > 0">
                    <div class="page-btn-wrap">
                        <div v-for="pageBtn in pageBtns" :key="pageBtn" class="page-btn"
                            :class="{ 'page-btn-active': pageBtn == currentPageNum }"
                            @click="getCardCollectListByPage(pageBtn)">
                            {{ pageBtn }}
                        </div>
                    </div>
                </div>

            </div>

            <!-- 未登入狀態 -->
            <div class="welcome-section" v-if="!isLogin">
                <div class="welcome-content">
                    <h2>{{ $t('htwbcard.welcome.title') }}</h2>
                    <p>{{ $t('htwbcard.welcome.message') }}</p>
                </div>
            </div>
        </div>

        <!-- 新增 CardCollect Modal -->
        <div v-show="showCreateCardCollectModal" class="shareCard-wrap">
            <div class="shareCard">
                <div class="close-btn">
                    <svg-icon icon-class="close" @click="showCreateCardCollectModal = false" />
                </div>

                <div class="shareCard-title">{{ $t('htwbcard.modals.createTitle') }}</div>

                <div class="shareCard-content">
                    <div class="rename-wrap">
                        <label class="rename-bar">
                            <input :placeholder="$t('htwbcard.forms.collectBoardName')" v-model="newCardCollectName" maxlength="20" />
                            <div class="delete-btn" v-show="newCardCollectName.length !== 0"
                                @click="newCardCollectName = ''">
                                <svg-icon icon-class="close" />
                            </div>
                        </label>
                    </div>
                </div>

                <!-- 設定開關 -->
                <div class="flex-container">
                    <div class="flex-item">{{ $t('htwbcard.forms.openSubmitLabel') }}</div>
                    <label class="switch flex-item">
                        <input type="checkbox" v-model="openSubmit">
                        <span class="slider round"></span>
                    </label>
                </div>


                <!-- 建立按鈕 -->
                <div class="shareCard-actions">
                    <button class="cancel-btn" @click="showCreateCardCollectModal = false">
                        {{ $t('htwbcard.forms.cancel') }}
                    </button>
                    <button class="confirm-btn" @click="createCardCollect()" :disabled="!newCardCollectName.trim()">
                        {{ $t('htwbcard.forms.create') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 編輯 CardCollect Modal -->
        <div v-show="showEditCardCollectModal" class="shareCard-wrap">
            <div class="shareCard">
                <div class="close-btn">
                    <svg-icon icon-class="close" @click="showEditCardCollectModal = false" />
                </div>

                <div class="shareCard-title">{{ $t('htwbcard.modals.editTitle') }}</div>

                <div class="shareCard-content">
                    <div class="rename-wrap">
                        <label class="rename-bar">
                            <input :placeholder="$t('htwbcard.forms.collectBoardName')" v-model="editCardCollectName" maxlength="20" />
                            <div class="delete-btn" v-show="editCardCollectName.length !== 0"
                                @click="editCardCollectName = ''">
                                <svg-icon icon-class="close" />
                            </div>
                        </label>
                    </div>
                </div>

                <!-- 設定開關 -->
                <div class="flex-container">
                    <div class="flex-item">{{ $t('htwbcard.forms.openSubmitLabel') }}</div>
                    <label class="switch flex-item">
                        <input type="checkbox" v-model="editOpenSubmit">
                        <span class="slider round"></span>
                    </label>
                </div>

                <!-- URL 顯示 -->
                <div class="url-wrap url-wrap-small" v-if="currentCardCollectURL">
                    <span class="url-title">URL：</span>{{ shortURLHost }}<span class="short-url">{{ currentCardCollectURL }}</span>
                    <span class="copy-btn" @click="copyShortUrl()">
                        <svg-icon icon-class="copy" />
                    </span>
                </div>

                <!-- 更新按鈕 -->
                <div class="shareCard-actions">
                    <button class="cancel-btn" @click="showEditCardCollectModal = false">
                        {{ $t('htwbcard.forms.cancel') }}
                    </button>
                    <button class="confirm-btn" @click="updateCardCollect()" :disabled="!editCardCollectName.trim()">
                        {{ $t('htwbcard.forms.update') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- CardCollect 操作選單 -->
        <div class="actions" v-show="showCardCollectActionMenu"
            :style="{ left: actionMenuPos.x + 'px', top: actionMenuPos.y + 'px' }">
            <div class="action-item" @click="openCardCollectSetting()">{{ $t('htwbcard.forms.setting') }}</div>
            <div class="action-item" @click="confirmDeleteCardCollect()">{{ $t('htwbcard.forms.delete') }}</div>
        </div>

        <!-- 刪除確認視窗 -->
        <div v-show="showDeleteConfirm" class="modal-overlay">
            <div class="modal-content delete-confirm-modal">
                <div class="modal-header">
                    <h3>{{ $t('htwbcard.modals.deleteTitle') }}</h3>
                    <button class="close-btn" @click="closeDeleteConfirm">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="modal-body">
                    <p class="confirm-text ">{{ $t('htwbcard.deleteConfirm.message', { name: cardCollectToDelete?.name }) }}</p>
                    <p class="warning-text danger-color" v-html="$t('htwbcard.deleteConfirm.warning')"></p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeDeleteConfirm" :disabled="isDeleting">
                        {{ $t('htwbcard.forms.cancel') }}
                    </button>
                    <button type="button" class="delete-btn" @click="confirmDelete" :disabled="isDeleting">
                        <span v-if="isDeleting" class="loading-spinner"></span>
                        {{ isDeleting ? $t('htwbcard.deleteConfirm.deleting') : $t('htwbcard.deleteConfirm.confirmDelete') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import api from '@/api'
import jwt_decode from 'jwt-decode'
import { useMainStore } from '@/stores/mainStore'
import SvgIcon from "../components/SvgIcon.vue"
import main from "@/api/main"
import confProfile from "@/api/profile"

var serverLocation = main.getServerLocation()

const tokenRefreshInterval = ref(null)

const { t } = useI18n()
const store = useMainStore()
const $q = useQuasar()
const router = useRouter()

// 響應式狀態
const isLogin = ref(false)
const showUserMenu = ref(false)
const profile = reactive({
    name: "",
    teammodelId: "",
    picture: ""
})

// CardCollect 相關狀態
const cardCollectList = ref([])
const showCreateCardCollectModal = ref(false)
const newCardCollectName = ref('')
const openSubmit = ref(true)

// CardCollect 編輯相關狀態
const showEditCardCollectModal = ref(false)
const editCardCollectName = ref('')
const editOpenSubmit = ref(true)

// 分頁相關狀態
const pageBtns = ref([])
const currentPageNum = ref(1)

// 搜尋相關狀態
const searchText = ref('')
const isSearching = ref(false)
const searchTimeout = ref(null)

// 操作選單相關
const showCardCollectActionMenu = ref(false)
const currentCardCollect = ref(null)
const actionMenuPos = reactive({ x: 0, y: 0 })

// 刪除確認相關
const showDeleteConfirm = ref(false)
const cardCollectToDelete = ref(null)
const isDeleting = ref(false)

// 短網址相關變數
const shortURLHost = ref(confProfile.shortUrlHost[serverLocation])
const currentCardCollectURL = ref("")

// 登入方法
const loginTMID = () => {
    api.main.loginTmdID()
}

// 登出方法
const logout = async () => {
    showUserMenu.value = false
    if (isLogin.value) {
        try {
            await api.main.logout()
            Message.info(t('htwbcard.messages.logoutSuccess'))
            isLogin.value = false
            store.setIsLogIn(false)

            // 清除本地儲存
            localStorage.removeItem('access_token')
            localStorage.removeItem('id_token')
            localStorage.removeItem('expires_in')
            localStorage.removeItem('profile')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_token_expiry')
            sessionStorage.clear()

            // 重置用戶資料
            profile.name = ""
            profile.teammodelId = ""
            profile.picture = ""
        } catch (error) {
            console.error('Logout error:', error)
            Message.error(t('htwbcard.messages.logoutFailed'))
        }
    }
}

// 切換用戶選單
const toggleUserMenu = () => {
    showUserMenu.value = !showUserMenu.value
}

// CardCollect 相關方法
const loadCardCollectList = async () => {
    if (!isLogin.value || !profile.teammodelId) return
    
    $q.loading.show()
    try {
        const requestData = {
            tmid: profile.teammodelId,
            pageSize: 15,
            pageNumber: 1,
            sortField: "createTime",
            sortOrder: "desc"
        }
        
        // 如果有搜尋文字，加入搜尋參數
        if (searchText.value.trim()) {
            requestData.searchText = searchText.value.trim()
        }
        
        const response = await api.cardAPI.getCardCollectById(requestData)
        console.log(response)
        
        if (response && response.status === 200 && response.data) {
            // 使用後端回傳的分頁資料
            cardCollectList.value = response.data.map(item => ({
                id: item.id,
                name: item.cardCollectName,
                creator: item.creator,
                editor: item.editor,
                openSubmit: item.openSubmit,
                createTime: item.createTime,
                updateTime: item.updateTime,
                stopSubmitTime: item.stopSubmitTime,
                isDeleting: item.isDeleting,
                urlClipCode: item.urlClipCode,
                groups: item.groups,
                pk: item.pk,
                ttl: item.ttl
            }))
            
            // 使用後端回傳的總頁數
            const totalPages = response.totalPages || 1
            pageBtns.value = Array.from({ length: totalPages }, (_, index) => index + 1)
        } else {
            cardCollectList.value = []
            pageBtns.value = []
        }
    } catch (error) {
        console.error('載入 CardCollect 列表失敗:', error)
        Message.error(t('htwbcard.messages.loadFailed'))
        cardCollectList.value = []
        pageBtns.value = []
    } finally {
        $q.loading.hide()
    }
}

// 分頁切換函數
const getCardCollectListByPage = async (page) => {
    currentPageNum.value = page
    
    $q.loading.show()
    try {
        const requestData = {
            tmid: profile.teammodelId,
            pageSize: 15,
            pageNumber: currentPageNum.value,
            sortField: "createTime",
            sortOrder: "desc"
        }
        
        // 如果有搜尋文字，加入搜尋參數
        if (searchText.value.trim()) {
            requestData.searchText = searchText.value.trim()
        }
        
        const response = await api.cardAPI.getCardCollectById(requestData)
        
        if (response && response.status === 200 && response.data) {
            cardCollectList.value = response.data.map(item => ({
                id: item.id,
                name: item.cardCollectName,
                creator: item.creator,
                editor: item.editor,
                openSubmit: item.openSubmit,
                createTime: item.createTime,
                updateTime: item.updateTime,
                stopSubmitTime: item.stopSubmitTime,
                isDeleting: item.isDeleting,
                urlClipCode: item.urlClipCode,
                groups: item.groups,
                pk: item.pk,
                ttl: item.ttl
            }))
            
            // 更新總頁數
            const totalPages = response.totalPages || 1
            pageBtns.value = Array.from({ length: totalPages }, (_, index) => index + 1)
        }
    } catch (error) {
        console.error('載入頁面資料失敗:', error)
        Message.error(t('htwbcard.messages.loadFailed'))
    } finally {
        $q.loading.hide()
    }
}

// 建立新的 CardCollect
const createCardCollect = async () => {
    if (!newCardCollectName.value.trim() || !profile.teammodelId) return

    try {
        // 先建立 CardCollect
        const createRequestData = {
            cardCollectName: newCardCollectName.value,
            creator: {
                id: profile.teammodelId,
                name: profile.name
            },
            openSubmit: openSubmit.value,
        }
        
        const createResponse = await api.cardAPI.createCardCollect(createRequestData)
        
        if (createResponse) {
            // 呼叫 API 生成簡碼
            const origData = `${window.location.origin}/cardcollect/${createResponse.id}`
            const clipCodeResponse = await api.mindmapAPI.getClipCode({
                origDataList: [{ origData: origData }],
                type: "cardcollect",
                useShortDigits: false,
                isPermanent: false,
                lang: "zh-tw",
                note: JSON.stringify({cardCollectName: newCardCollectName.value})
            })
            
            const urlClipCode = clipCodeResponse.clipCodePairs[0].clipCode

            // 更新 CardCollect 加入簡碼
            const updateRequestData = {
                id: createResponse.id,
                editor: {
                    id: profile.teammodelId,
                    name: profile.name
                },
                urlClipCode: urlClipCode
            }
            
            await api.cardAPI.updateCardCollect(updateRequestData)

            showCreateCardCollectModal.value = false
            resetCreateForm()
            Message.success(t('htwbcard.messages.createSuccess'))

            // 跳轉到該 CardCollect 頁面並自動打開分享設定
            router.push(`/cardcollect/${createResponse.id}?autoShare=true`)
        }
    } catch (error) {
        console.error('建立 CardCollect 失敗:', error)
        Message.error(t('htwbcard.messages.createFailed'))
    }
}

// 重置建立表單
const resetCreateForm = () => {
    newCardCollectName.value = ''
    openSubmit.value = true
}

// 更新 CardCollect
const updateCardCollect = async () => {
    if (!editCardCollectName.value.trim() || !currentCardCollect.value || !profile.teammodelId) return

    try {
        const requestData = {
            id: currentCardCollect.value.id,
            cardCollectName: editCardCollectName.value,
            openSubmit: editOpenSubmit.value,
            editor: {
                id: profile.teammodelId,
                name: profile.name
            }
        }
        
        const response = await api.cardAPI.updateCardCollect(requestData)
        
        if (response) {
            showEditCardCollectModal.value = false
            resetEditForm()
            await getCardCollectListByPage(currentPageNum.value) // 重新載入當前頁面資料
            Message.success(t('htwbcard.messages.updateSuccess'))
        }
    } catch (error) {
        console.error('更新 CardCollect 失敗:', error)
        Message.error(t('htwbcard.messages.updateFailed'))
    }
}

// 重置編輯表單
const resetEditForm = () => {
    editCardCollectName.value = ''
    editOpenSubmit.value = true
}

// 進入 CardCollect
const enterCardCollect = (cardCollect) => {
    router.push(`/cardcollect/${cardCollect.id}`)
}

// 開啟 CardCollect 操作選單
const openCardCollectActionMenu = (cardCollect, event) => {
    event.stopPropagation()
    currentCardCollect.value = cardCollect
    actionMenuPos.x = event.clientX-80
    actionMenuPos.y = event.clientY+10
    showCardCollectActionMenu.value = true
}

// 開啟 CardCollect 設定
const openCardCollectSetting = () => {
    showCardCollectActionMenu.value = false
    if (currentCardCollect.value) {
        editCardCollectName.value = currentCardCollect.value.name
        editOpenSubmit.value = currentCardCollect.value.openSubmit
        currentCardCollectURL.value = currentCardCollect.value.urlClipCode || ""
        showEditCardCollectModal.value = true
    }
}

// 開啟刪除確認視窗
const confirmDeleteCardCollect = () => {
    showCardCollectActionMenu.value = false
    if (currentCardCollect.value) {
        cardCollectToDelete.value = currentCardCollect.value
        showDeleteConfirm.value = true
    }
}

// 關閉刪除確認視窗
const closeDeleteConfirm = () => {
    showDeleteConfirm.value = false
    cardCollectToDelete.value = null
}

// 確認刪除 CardCollect
const confirmDelete = async () => {
    if (!cardCollectToDelete.value || isDeleting.value) return
    
    isDeleting.value = true
    
    try {
        console.log('開始刪除收集板:', cardCollectToDelete.value)
        
        // 呼叫刪除 API
        const response = await api.cardAPI.deleteCardCollect({
            id: cardCollectToDelete.value.id
        })
        
        console.log('刪除 API 回應:', response)
        
        if (response && response.status === 200) {
            Message.success(t('htwbcard.messages.deleteSuccess'))
            closeDeleteConfirm()
            
            // 重新載入收集板列表
            await getCardCollectListByPage(currentPageNum.value)
        } else {
            throw new Error('刪除請求失敗')
        }
    } catch (error) {
        console.error('刪除收集板失敗:', error)
        Message.error(t('htwbcard.messages.deleteFailed'))
    } finally {
        isDeleting.value = false
    }
}

// 搜尋相關方法
const onSearchInput = () => {
    // 清除之前的搜尋定時器
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
    }
    
    // 設定新的搜尋定時器（防抖動）
    searchTimeout.value = setTimeout(() => {
        performSearch()
    }, 500)
}

const performSearch = async () => {
    isSearching.value = true
    currentPageNum.value = 1 // 搜尋時重置到第一頁
    
    try {
        await loadCardCollectList()
    } finally {
        isSearching.value = false
    }
}

const clearSearch = async () => {
    searchText.value = ''
    isSearching.value = false
    currentPageNum.value = 1
    
    // 清除搜尋定時器
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
        searchTimeout.value = null
    }
    
    // 重新載入所有資料
    await loadCardCollectList()
}

// 格式化時間
const getTime = (timestamp) => {
    if (!timestamp) return ''
    // 如果是 Unix 時間戳（秒），需要轉換為毫秒
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('zh-TW')
}


// 複製短網址
const copyShortUrl = () => {
    const url = shortURLHost.value + currentCardCollectURL.value;
    const input = document.createElement('input');
    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('Copy');
    document.body.removeChild(input);
    Message.info(t("htwbcard.messages.alreadyCopied"));
}

// 初始化認證
const initializeAuth = async () => {
    try {
        const res = await api.main.getTokenUnlogin()
        const exp = jwt_decode(res.auth_token).exp
        localStorage.setItem("auth_token", res.auth_token)
        localStorage.setItem("auth_token_expiry", exp)
    } catch (error) {
        console.error("Error fetching auth token:", error)
    }
}

// 檢查自動登入
const checkAutoLogin = () => {
    const savedProfile = localStorage.getItem("profile")
    const savedToken = localStorage.getItem("access_token")

    if (savedProfile && savedToken) {
        try {
            const profileData = JSON.parse(savedProfile)
            isLogin.value = true
            profile.name = profileData.name
            profile.teammodelId = profileData.id || profileData.teammodelId
            profile.picture = profileData.picture
            store.setIsLogIn(true)
        } catch (error) {
            console.error("Auto login error:", error)
        }
    }
}

// 處理 OAuth 回調
const handleOAuthCallback = async () => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get("code")

    if (code) {
        try {
            const res = await api.main.getToken()
            localStorage.setItem("id_token", res.id_token)
            localStorage.setItem("access_token", res.access_token)
            localStorage.setItem("expires_in", res.expires_in)

            const decoded = jwt_decode(res.id_token)

            isLogin.value = true
            store.setIsLogIn(true)
            profile.name = decoded.name
            profile.teammodelId = decoded.sub
            profile.picture = decoded.picture

            localStorage.setItem("profile", JSON.stringify({
                name: profile.name,
                id: profile.teammodelId,
                picture: profile.picture,
            }))

            // 清除 URL 中的 code 參數
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.delete('code')
            newUrl.searchParams.delete('state')
            window.history.replaceState({}, document.title, newUrl.toString())

            Message.success(t('htwbcard.messages.loginSuccess'))
        } catch (error) {
            console.error("OAuth callback error:", error)
            Message.error(t('htwbcard.messages.loginFailed'))
        }
    }
}

const getCardCollectSas = async () => {
    try {
        const res = await api.cardAPI.getCardCollectSas()
        console.log(res)
    } catch (error) {
        console.error("Error fetching card collect SAS:", error)
    }
}

const startTokenRefreshInterval = () => {
    tokenRefreshInterval.value = setInterval(async () => {
        // 檢查auth_token是否即將過期
        const authExpiry = localStorage.getItem("auth_token_expiry");
        const now = Date.now();
        const refreshThreshold = 10 * 1000; // 提前 10 秒刷新

        if (authExpiry) {
            const exp = parseInt(authExpiry) * 1000; // 將 exp 轉換為毫秒
            if (exp - now < refreshThreshold) {
                localStorage.removeItem("auth_token");
                localStorage.removeItem("auth_token_expiry");
                await initializeAuth();
            }
        }

        // 檢查 SAS Token 是否即將過期
        const sasExpiry = sessionStorage.getItem("card_sas_expiry");
        if (sasExpiry) {
            const sasExpiryMs = Date.parse(sasExpiry);
            if (sasExpiryMs - now < refreshThreshold) {
                console.log("SAS Token is about to expire. Refreshing...");
                await getCardCollectSas();
            }
        }
    }, 60 * 1000); // 每分檢查一次
}

// 組件掛載時執行
onMounted(async () => {
    // 初始化認證
    await initializeAuth()
    await getCardCollectSas()
    // 檢查自動登入
    checkAutoLogin()
    startTokenRefreshInterval()
    // 處理 OAuth 回調
    await handleOAuthCallback()
    // 載入 CardCollect 資料
    if (isLogin.value) {
        await loadCardCollectList()
    }
})

// 點擊外部關閉選單
const handleClickOutside = (event) => {
    // 關閉用戶選單
    if (!event.target.closest('.user-section')) {
        showUserMenu.value = false
    }
    
    // 關閉 CardCollect 操作選單
    if (!event.target.closest('.actions') && !event.target.closest('.more-btn')) {
        showCardCollectActionMenu.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

</script>
<style lang="less">
@import "../assets/HTwbCardCSS/HTwbCardHome.less";
</style>